import { LocationType } from "../../views/contacts/ContactTypes";
import { UserType } from "../../views/users/UserTypes";
import { EquipmentType } from '../scheduling/SchedulingTypes';

export interface JobDescriptionType {
    id: number | null;
    uuid?: string;
    created: string | null;
    job: number;
    appointments: number[]
    // appointment_ids: number[];
    type?: 'instruction' | 'activities' | 'additional_work';
    entered_at?: string | null;
    source: 'office' | 'field';
    description: string;
    created_by_user_hash?: string;
    created_by_user?: number;
    created_by_user_name?: string | null;
    // tasks?: JobTask[];  // To be implemented
    deleted: boolean;
};

export const createNewJobDescription = (jobId: number, uuid: string, userHash: string): JobDescriptionType => ({
    id: null,
    uuid: uuid,
    created: null,
    job: jobId,
    appointments: [],
    // appointment_ids: [],
    type: 'instruction',
    entered_at: null,
    source: 'office',
    description: '',
    created_by_user_hash: userHash,
    deleted: false
});

export interface JobItemType {
    id: number | null;
    uuid?: string;
    job: number;
    appointment: number | null;
    product: number | null;
    name: string;
    number: string | null;
    unit: string | null;
    sale_price: string | null;
    sale_price_currency: string | null;
    total_price: number | null;
    purchase_price: string | null;
    purchase_price_currency: string | null;
    total_purchase_price: number | null;
    vat_rate: number | null;
    ledger_account: number | null;
    is_inventory: boolean;
    quantity_reserved: number | null;
    quantity_registrated_string: string | null;
    quantity_registrated: number | null;
    date_registrated: string | null;
    position: number | null;
    deleted: boolean;
};

export const createNewJobItem = (jobId: number, uuid: string, position: number): JobItemType => ({
    id: null,
    uuid: uuid,
    job: jobId,
    appointment: null,
    product: null,
    name: '',
    number: null,
    unit: null,
    sale_price: null,
    sale_price_currency: 'EUR',
    total_price: null,
    purchase_price: null,
    purchase_price_currency: 'EUR',
    total_purchase_price: null,
    vat_rate: null,
    ledger_account: null,
    is_inventory: false,
    quantity_reserved: null,
    quantity_registrated_string: null,
    quantity_registrated: null,
    date_registrated: null,
    position: position,
    deleted: false,
});

export interface JobTaskType {
    id: number | null;
    uuid?: string;
    job: number | null;
    location: number | null;
    appointment: number | null;
    description: string;
    form_id?: number | null
    form_sheet?: {
        id: number | null
        form_id: number
        form_name: string
        status: string
    } | null
    required: boolean;
    created_by_user: number | null;
    checked: boolean;
    checked_by_user: number | null;
    checked_at: string | null;
    ordering: number | null;
    deleted: boolean;
};

export const createNewJobTask = (uuid: string, ordering: number, jobId?: number, appointmentId?: number): JobTaskType => ({
    id: null,
    uuid: uuid,
    job: jobId ?? null,
    location: null,
    appointment: appointmentId ?? null,
    description: '',
    form_id: null,
    form_sheet: null,
    required: false,
    created_by_user: null,
    checked: false,
    checked_by_user: null,
    checked_at: null,
    ordering: ordering,
    deleted: false,
});

export interface TimeBlockType {
    id: number;
    name: string | null;
    start_time: string | null;
    end_time: string | null;
    division: number | null;
};

export interface AppointmentType {
    id: number | null;
    uuid?: string;
    start_datetime: string | null;
    end_datetime: string | null;
    start_date: string | null;
    end_date: string | null;
    start_time: string | null;
    end_time: string | null;
    time_block: number | null;
    whole_day: boolean;
    duration: number | null;
    duration_string: string | null;
    break_duration?: number | string | null;
    break_duration_string?: string | null;
    status: string;
    custom_color: string | null;
    locked: boolean;
    job: Job | null;
    deleted: boolean;
    division: number | null;
    assignees: UserType[] | null;
    assignee_ids: number[];
    equipment?: EquipmentType[] | null;
    equipment_ids?: number[];
    foreman_id?: number | null;
};

export const createNewAppointment = (job: Job, uuid: string): AppointmentType => ({
    id: null,
    uuid: uuid,
    start_datetime: null,
    end_datetime: null,
    start_date: null,
    end_date: null,
    start_time: null,
    end_time: null,
    whole_day: false,
    time_block: null,
    duration: null,
    duration_string: null,
    break_duration: null,
    break_duration_string: null,
    status: 'scheduled',
    custom_color: null,
    locked: false,
    job: job,
    deleted: false,
    division: null,
    assignees: [],
    assignee_ids: [],
    equipment: [],
    equipment_ids: [],
    foreman_id: null,
});

export interface Job {
    id: number | null;
    uuid?: string;
    created: string | null;
    modified: string | null;
    number: string | null;
    subject: string | null
    internal_reference: string | null;
    external_reference: string | null;
    client_name: string | null;
    labels: number[];
    services: number[];
    appointments: AppointmentType[] | null;
    main_description: string | null;
    descriptions: JobDescriptionType[] | null;
    project: number | null;
    project_job_capacities?: {
        id: number | null;
        created: string | null;
        modified: string | null;
        project_id: number | null;
        job_id: number | null;
        date: string | null;
        required_employees: number | null;
        deleted: boolean;
    }[] | null;
    organization: number | null;
    person: number | null;
    work_location: LocationType | null;
    work_location_address: string | null;
    target_date: string | null;
    estimated_duration: string | null;
    billing_type: string;
    workflow: {
        id: number;
        name: string;
    };
    status: number | null;
    status_name: string | null;
    status_condition: string | null;
    resolution: string | null;
    ordering: number | null;
    deleted: boolean;
    division: number | null;
    items?: JobItemType[] | null;
    timerows?: { id: number | null, deleted: boolean }[] | null;
    is_invoiced?: boolean;
};

export interface JobScheme {
    id: number;
    connected_feature: 'job';
    name: string;
    is_active: boolean;
    division: number | null;
}